import type {
	CircleLayerSpecification,
	ExpressionSpecification,
	FillLayerSpecification,
	LineLayerSpecification,
	SymbolLayerSpecification,
} from "mapbox-gl";
import { MAP_ENTITY_COLORS } from "@/features/map/lib/map-entity-colors";

/**
 * Map cluster / MVT fill colors per layer — aligned with Destiny MapMarker,
 * ClusterMarker, Legend, and MapShell GeoJSON line/fill colors.
 */
export const MAP_CLUSTER_COLORS = {
	jobs: MAP_ENTITY_COLORS.job,
	tickets: MAP_ENTITY_COLORS.ticket,
	assets: MAP_ENTITY_COLORS.asset,
	records: MAP_ENTITY_COLORS.record,
} as const;

/**
 * Pixel step between stacked entity crescents (Figma top-right fan).
 * Applied via MVT layer `circle-translate` for cross-type co-location.
 */
export const MVT_CLUSTER_STACK_STEP_PX = 4;

/**
 * Screen-pixel fan offsets for co-located MVT entity cluster *layers*.
 * Mapbox `circle-translate` is layer-wide (+y down): jobs and tickets share
 * the front anchor; assets/records peek top-right behind them.
 */
export const MVT_CLUSTER_STAGGER_OFFSETS: Record<
	"jobs-mvt" | "tickets-mvt" | "assets-mvt" | "records-mvt",
	[number, number]
> = {
	"jobs-mvt": [0, 0],
	"tickets-mvt": [0, 0],
	"assets-mvt": [MVT_CLUSTER_STACK_STEP_PX, -MVT_CLUSTER_STACK_STEP_PX],
	"records-mvt": [
		MVT_CLUSTER_STACK_STEP_PX * 2,
		-MVT_CLUSTER_STACK_STEP_PX * 2,
	],
};

/** MVT sources render in this order so later entries paint on top (jobs last). */
export const MVT_LAYER_RENDER_ORDER = [
	"tickets-mvt",
	"assets-mvt",
	"records-mvt",
	"jobs-mvt",
] as const;

/** Layer legend / filter checkbox order in the top-left control stack. */
export const MVT_LAYER_LEGEND_ORDER = [
	"jobs-mvt",
	"tickets-mvt",
	"assets-mvt",
	"records-mvt",
] as const;

/**
 * Destiny Map/ClusterMarker Ellipse 1 stroke — same token as Map/Marker point
 * outline (`Colors/Gray light mode/50` / `#F5F5F5`).
 */
const CLUSTER_ELLIPSE_STROKE = "#f5f5f5";
/** Destiny Map/Marker point outline (`Colors/Gray light mode/50`). */
const MARKER_POINT_STROKE = "#f5f5f5";
/** Destiny Map/Marker active point fill (`Colors/Blue gray/700`). */
const MARKER_POINT_ACTIVE_FILL = "#213a53";
/** Matches Destiny ClusterMarker `text-fg-white`. */
const CLUSTER_COUNT_TEXT = "#ffffff";

/**
 * Circle paint for server-side MVT clusters.
 *
 * Outer diameters match Destiny ClusterMarker Quantity bands (32 / 40 / 48).
 * Mapbox draws stroke outside the radius, so fill radii are inset by the 2px
 * Ellipse 1 ring: 14 / 18 / 22 → outer 16 / 20 / 24.
 */
export function createMvtClusterCirclePaint(
	color: string,
): NonNullable<CircleLayerSpecification["paint"]> {
	return {
		"circle-color": color,
		"circle-radius": ["step", ["get", "point_count"], 14, 10, 18, 100, 22],
		"circle-stroke-width": 2,
		"circle-stroke-color": CLUSTER_ELLIPSE_STROKE,
		"circle-translate": [0, 0],
		"circle-translate-anchor": "viewport",
	};
}

/** Symbol layout for cluster count labels. */
export function createMvtClusterCountLayout(): NonNullable<
	SymbolLayerSpecification["layout"]
> {
	return {
		"text-field": ["to-string", ["get", "point_count"]],
		"text-font": ["DIN Offc Pro Bold", "Arial Unicode MS Bold"],
		"text-size": 12,
		"text-anchor": "center",
		"text-allow-overlap": true,
		"text-ignore-placement": true,
	};
}

/** Symbol paint for cluster count labels (white on colored circle — ClusterMarker). */
export function createMvtClusterCountPaint(): NonNullable<
	SymbolLayerSpecification["paint"]
> {
	return {
		"text-color": CLUSTER_COUNT_TEXT,
		"text-translate": [0, 0],
		"text-translate-anchor": "viewport",
	};
}

/** Mapbox feature-state expression — true when the open MVT popup targets this feature. */
const MVT_FEATURE_SELECTED: ExpressionSpecification = [
	"boolean",
	["feature-state", "selected"],
	false,
];

/**
 * Unclustered point paint matching Destiny Map/Marker's 16px SVG:
 * a 12px entity-colored fill with a 2px gray-100 outer stroke. Active markers
 * keep the same 16px diameter and switch their fill to brand-700.
 */
export function createMvtUnclusteredPointPaint(
	color: string,
): NonNullable<CircleLayerSpecification["paint"]> {
	return {
		"circle-color": [
			"case",
			MVT_FEATURE_SELECTED,
			MARKER_POINT_ACTIVE_FILL,
			color,
		],
		// Mapbox puts strokes outside the radius: 6px + 2px = 8px outer radius.
		"circle-radius": 6,
		"circle-stroke-width": 2,
		"circle-stroke-color": MARKER_POINT_STROKE,
	};
}

/**
 * Circle paint for LineString / Polygon vertices (Mapbox draws a circle at
 * each vertex). White fill + entity stroke — distinct from unclustered pins.
 */
export function createMvtVertexPaint(
	color: string,
): NonNullable<CircleLayerSpecification["paint"]> {
	return {
		"circle-color": "#ffffff",
		"circle-radius": 5,
		"circle-stroke-color": color,
		"circle-stroke-width": 2,
	};
}

/**
 * Circle paint for the single active vertex overlay (Destiny MapMarker active
 * fill: brand-700 + gray-100 stroke). Matches default vertex size.
 */
export function createActiveVertexCirclePaint(): NonNullable<CircleLayerSpecification["paint"]> {
	return {
		"circle-color": MARKER_POINT_ACTIVE_FILL,
		"circle-radius": 5,
		"circle-stroke-color": MARKER_POINT_STROKE,
		"circle-stroke-width": 2,
	};
}

/**
 * Line paint for unclustered MVT LineString / Polygon outlines —
 * matches MapShell GeoJSON shape line layer (`line-width: 3`).
 * When selected: brand-700 stroke (same as active point) and wider width.
 */
export function createMvtLinePaint(
	color: string,
): NonNullable<LineLayerSpecification["paint"]> {
	return {
		"line-color": [
			"case",
			MVT_FEATURE_SELECTED,
			MARKER_POINT_ACTIVE_FILL,
			color,
		],
		"line-width": ["case", MVT_FEATURE_SELECTED, 5, 3],
	};
}

/**
 * Fill paint for unclustered MVT polygons —
 * matches MapShell GeoJSON shape fill (`fill-opacity: 0.25`).
 * When selected: brand-700 fill (same as active point) and brighter opacity.
 */
export function createMvtFillPaint(
	color: string,
): NonNullable<FillLayerSpecification["paint"]> {
	return {
		"fill-color": [
			"case",
			MVT_FEATURE_SELECTED,
			MARKER_POINT_ACTIVE_FILL,
			color,
		],
		"fill-opacity": ["case", MVT_FEATURE_SELECTED, 0.45, 0.25],
	};
}

/** Circle paint for client-side GeoJSON clusters (same visual as MVT). */
export function createGeoJsonClusterCirclePaint(
	color: string,
): NonNullable<CircleLayerSpecification["paint"]> {
	return createMvtClusterCirclePaint(color);
}

/** Symbol layout for GeoJSON cluster counts. */
export function createGeoJsonClusterCountLayout(): NonNullable<
	SymbolLayerSpecification["layout"]
> {
	return {
		"text-field": ["get", "point_count_abbreviated"],
		"text-font": ["DIN Offc Pro Bold", "Arial Unicode MS Bold"],
		"text-size": 12,
	};
}
